import { apiCall } from "@/api/client";
import { toSnakeCase } from "@/api/payloadCase";
import type {
  GatewayCatalogItem,
  GatewayInstanceKind,
  GatewayRecipe,
  ListCatalogResponse,
} from "@/types/api";

// Normalise the `payload` blob on the way in and out of the catalog admin
// API. The admin Lambda converts every response key to camelCase via the
// shared `json_response` helper, but the per-instance `payload` is opaque
// and consumed in snake_case by the Rust dataplane. Applying `toSnakeCase`
// symmetrically on reads + writes means the JSON editor and the wire shape
// always agree with what the dataplane sees. See payloadCase.ts for the
// conversion contract.
//
// A recipe's fixed fields (description, osSupported, bootstrap, …) are NOT
// run through this — they're typed camelCase fields already produced
// correctly by json_response. Only ``components[].capability`` needs it:
// its keys are the free-form param keys a component author chose (e.g.
// "max_supported"), the same "identifier in key position" trap as a
// component's own body/capability (see api/components.ts's normalise()).
function normaliseRecipe(recipe: GatewayRecipe | null): GatewayRecipe | null {
  if (!recipe) return recipe;
  return {
    ...recipe,
    components: recipe.components.map((ref) => ({
      ...ref,
      capability: toSnakeCase(ref.capability),
    })),
  };
}

function normaliseItem(item: GatewayCatalogItem): GatewayCatalogItem {
  return {
    ...item,
    payload: item.payload ? toSnakeCase(item.payload) : item.payload,
    recipe: normaliseRecipe(item.recipe),
  };
}

/** Indicative JSON Schema served by the catalog_admin Lambda. */
export interface GatewaySchemaResponse {
  schema: Record<string, unknown>;
}

/**
 * A versioned instance entry's identity triple — kind + model + version.
 * Sections live in their own subtree (no version) and are addressed by name
 * only via {@link sectionPath}.
 */
export interface InstanceRef {
  kind: GatewayInstanceKind;
  model: string;
  version: string;
}

/** Backend instance route — used by both the API client and the GUI router. */
export function instancePath(ref: InstanceRef): string {
  return `/catalog/gateways/instances/${encodeURIComponent(ref.kind)}/${encodeURIComponent(ref.model)}/${encodeURIComponent(ref.version)}`;
}

/** Backend section route — sections are addressed by name only. */
export function sectionPath(name: string): string {
  return `/catalog/gateways/sections/${encodeURIComponent(name)}`;
}

export async function listGateways(token: string) {
  const res = await apiCall<ListCatalogResponse>("/catalog/gateways", { token });
  return { ...res, items: res.items.map(normaliseItem) };
}

/** One row per versioned catalog entry — sections carry no allowlist. */
export interface CatalogUsageEntry {
  kind: GatewayInstanceKind;
  model: string;
  version: string;
  tenantIds: string[];
}

/**
 * Tenants referencing each catalog entry, in a single call. The backend
 * sweeps the tenant registry once for the whole catalog: that sweep costs
 * the same whether it answers about one entry or all of them, so asking
 * per row would be the same sweep multiplied by the number of rows shown.
 */
export function listGatewayUsage(token: string) {
  return apiCall<{ items: CatalogUsageEntry[] }>("/catalog/gateways/usage", {
    token,
  });
}

export async function getInstance(token: string, ref: InstanceRef) {
  const res = await apiCall<GatewayCatalogItem>(instancePath(ref), { token });
  return normaliseItem(res);
}

export async function createInstance(
  token: string,
  ref: InstanceRef,
  payload: Record<string, unknown>,
) {
  const res = await apiCall<GatewayCatalogItem>(instancePath(ref), {
    method: "POST",
    body: { payload: toSnakeCase(payload) },
    token,
  });
  return normaliseItem(res);
}

export async function updateInstance(
  token: string,
  ref: InstanceRef,
  payload: Record<string, unknown>,
) {
  const res = await apiCall<GatewayCatalogItem>(instancePath(ref), {
    method: "PUT",
    body: { payload: toSnakeCase(payload) },
    token,
  });
  return normaliseItem(res);
}

/**
 * Customer instances are composed from catalog components, not posted as a
 * monolithic blob (see the module docstring on
 * lambdas/catalog_admin/handler.py): POST/PUT take ``{"recipe": ...}``
 * instead of ``{"payload": ...}``, and the recipe's own fields are Pydantic
 * attribute names — snake_case, unlike the camelCase this GUI otherwise
 * works in. ``toSnakeCase`` here does the same job it does for ``payload``
 * above; the ``components[].capability`` keys it touches were already
 * snake_case, so the pass is a no-op for them.
 */
export async function createCustomerType(
  token: string,
  model: string,
  version: string,
  recipe: GatewayRecipe,
) {
  const res = await apiCall<GatewayCatalogItem>(
    instancePath({ kind: "customer", model, version }),
    { method: "POST", body: { recipe: toSnakeCase(recipe) }, token },
  );
  return normaliseItem(res);
}

export async function updateCustomerType(
  token: string,
  model: string,
  version: string,
  recipe: GatewayRecipe,
) {
  const res = await apiCall<GatewayCatalogItem>(
    instancePath({ kind: "customer", model, version }),
    { method: "PUT", body: { recipe: toSnakeCase(recipe) }, token },
  );
  return normaliseItem(res);
}

/**
 * Tenants whose delivered configuration references this exact instance
 * triple. Same source as the DELETE referential guard, so both agree on
 * what "used by" means.
 *
 * This is the impact aggregate for the type editor, and it deliberately
 * starts from the type's identity rather than from the components a recipe
 * happens to check: unchecking every optional component doesn't shrink a
 * save's blast radius, it *is* the largest one the editor can produce.
 */
export function getInstanceUsage(token: string, ref: InstanceRef) {
  return apiCall<{ tenantIds: string[] }>(`${instancePath(ref)}/usage`, {
    token,
  });
}

export function deleteInstance(token: string, ref: InstanceRef) {
  return apiCall<{
    kind: GatewayInstanceKind;
    model: string;
    version: string;
    deleted: boolean;
  }>(instancePath(ref), { method: "DELETE", token });
}

export async function getSection(token: string, name: string) {
  const res = await apiCall<GatewayCatalogItem>(sectionPath(name), { token });
  return normaliseItem(res);
}

export async function createSection(
  token: string,
  name: string,
  payload: Record<string, unknown>,
) {
  const res = await apiCall<GatewayCatalogItem>(sectionPath(name), {
    method: "POST",
    body: { payload: toSnakeCase(payload) },
    token,
  });
  return normaliseItem(res);
}

export async function updateSection(
  token: string,
  name: string,
  payload: Record<string, unknown>,
) {
  const res = await apiCall<GatewayCatalogItem>(sectionPath(name), {
    method: "PUT",
    body: { payload: toSnakeCase(payload) },
    token,
  });
  return normaliseItem(res);
}

export function deleteSection(token: string, name: string) {
  return apiCall<{ section: string; deleted: boolean }>(sectionPath(name), {
    method: "DELETE",
    token,
  });
}

/**
 * List every version of a single instance model — used by the version
 * history panel in the editor. Backed by the existing GET
 * /catalog/gateways/instances/{kind}/{model} route (``list_versions``).
 */
export function listInstanceVersions(
  token: string,
  kind: GatewayInstanceKind,
  model: string,
) {
  return apiCall<ListCatalogResponse>(
    `/catalog/gateways/instances/${encodeURIComponent(kind)}/${encodeURIComponent(model)}`,
    { token },
  );
}

/**
 * Indicative JSON Schema for an instance kind (cloud, customer).
 * Returns 404 when no schema is registered for the kind on the backend —
 * the caller is expected to treat that as "no hints" rather than a hard
 * error.
 */
export function getInstanceSchema(token: string, kind: GatewayInstanceKind) {
  return apiCall<GatewaySchemaResponse>(
    `/catalog/gateway-schemas/instances/${encodeURIComponent(kind)}`,
    { token },
  );
}

/** Indicative JSON Schema for a known section name. */
export function getSectionSchema(token: string, name: string) {
  return apiCall<GatewaySchemaResponse>(
    `/catalog/gateway-schemas/sections/${encodeURIComponent(name)}`,
    { token },
  );
}
